import { Search, User, Menu } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AlertBadge } from './alerts/AlertBadge'

interface SuperAdminHeaderProps {
  title: string
  subtitle?: string
  userName?: string
  userEmail?: string
  actions?: React.ReactNode
  onMenuToggle?: () => void
}

export function SuperAdminHeader({
  title,
  subtitle,
  userName,
  userEmail,
  actions,
  onMenuToggle,
}: SuperAdminHeaderProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()

  return (
    <header className="bg-white border-b border-gray-200 px-4 md:px-5 py-3">
      <div className="flex items-center justify-between gap-2">
        {/* Hamburger (mobile only) + Title */}
        <div className="flex items-center gap-3 min-w-0">
          {onMenuToggle && (
            <button
              onClick={onMenuToggle}
              className="flex md:hidden flex-shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate">{title}</h1>
            {subtitle && <p className="text-xs md:text-sm text-gray-500 mt-0.5 truncate">{subtitle}</p>}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Custom Actions */}
          {actions}

          {/* Search - hidden on mobile */}
          <div className="relative hidden xl:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-48 lg:w-64 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          {/* Alerts */}
          <AlertBadge onClick={() => navigate({ to: '/super-admin/alerts' })} />

          {/* User Menu */}
          <div className="flex items-center gap-2 pl-2 lg:pl-3 border-l border-gray-200">
            <div className="w-8 h-8 md:w-9 md:h-9 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
            </div>
            <div className="text-right hidden xl:block">
              <p className="text-sm font-medium text-gray-900 leading-tight">{userName || 'Super Admin'}</p>
              <p className="text-xs text-gray-500">{userEmail || 'admin@secbrain.com'}</p>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
